// src/contexts/AuthContext.js
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../utils/supabaseClient';

const AuthContext = createContext();
const STUDENT_SESSION_KEY = 'krm_student_session';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [studentUser, setStudentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to fetch user profile
  const fetchUserProfile = async (userId) => {
    if (!userId) {
      setProfile(null);
      return null;
    }
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();
      
      if (error) throw error;
      setProfile(data);
      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
      return null;
    }
  };

  // Verify student session via backend (bypasses RLS)
  const verifyStudentSession = async (rno) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const res = await fetch(`${API_BASE_URL}/api/auth/student-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rollNo: rno }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Server error (500, etc.) — treat as network issue, don't log out
      if (!res.ok) {
        return { success: false, networkError: true };
      }

      return await res.json();
    } catch {
      // Network error, timeout, DNS failure — don't log out
      return { success: false, networkError: true };
    }
  };

  useEffect(() => {
    // Check active sessions and sets the user
    const checkAuth = async () => {
      try {
        setLoading(true);

        // Restore student session from localStorage (no network call on refresh)
        const savedStudent = localStorage.getItem(STUDENT_SESSION_KEY);
        if (savedStudent) {
          try {
            const parsed = JSON.parse(savedStudent);
            if (parsed.Rno && parsed.Name) {
              setStudentUser({ Rno: parsed.Rno, Name: parsed.Name, Class: parsed.Class, role: 'student' });
              setLoading(false);
              return;
            }
          } catch { /* corrupted data */ }
          localStorage.removeItem(STUDENT_SESSION_KEY);
          setStudentUser(null);
        }

        const { data: { session }, error } = await supabase.auth.getSession();

        // Network/server error while refreshing token — keep any cached session, don't log out
        if (error) {
          console.warn('⚠️ Auth session check failed (network/server issue):', error.message);
          if (session?.user) {
            setUser(session.user);
            fetchUserProfile(session.user.id);
          }
          // If no cached session, stay in current state (don't force-null on transient error)
          return;
        }

        if (session?.user) {
          setUser(session.user);
          fetchUserProfile(session.user.id)
        } else {
          setUser(null);
          setProfile(null);
        }
      } catch (error) {
        // Network error (server unreachable) — do NOT log out, keep current state
        console.warn('⚠️ Error checking auth (keeping session):', error?.message || error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Re-verify student session when user returns to the tab/app
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const saved = localStorage.getItem(STUDENT_SESSION_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          verifyStudentSession(parsed.Rno).then(data => {
            if (data.networkError) {
              // Server/network unreachable — keep session, don't log out
              console.warn('⚠️ Network error during student verify — keeping session');
            } else if (!data.success) {
              // Student not found or deactivated — logout
              setStudentUser(null);
              localStorage.removeItem(STUDENT_SESSION_KEY);
            }
          });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for changes in auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (studentUser) return; // Don't override student session

        // Only clear the session on an EXPLICIT sign-out (user clicked logout).
        // Transient token-refresh failures (network blips, multi-tab token rotation)
        // should NOT force a logout.
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
        } else if (session?.user) {
          // SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION, USER_UPDATED
          setUser(session.user);
          fetchUserProfile(session.user.id);
        }
        setLoading(false);
      }
    );

    return () => {
      subscription?.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Student sign-in: Roll No + password via backend API (service role, bypasses RLS)
  const studentSignIn = async (rollNo, password) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/student-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rollNo, password }),
      });

      const data = await res.json();

      if (!data.success) {
        return { error: { message: data.message } };
      }

      // Successful login
      const studentData = { Rno: data.student.Rno, Name: data.student.Name, Class: data.student.Class, role: 'student' };
      setStudentUser(studentData);
      localStorage.setItem(STUDENT_SESSION_KEY, JSON.stringify(studentData));
      return { data: studentData, error: null };
    } catch (err) {
      console.error('Student sign-in error:', err);
      return { error: { message: 'Something went wrong. Please try again later.' } };
    }
  };

  const value = {
    signIn: async (email, password) => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (data?.user) {
        const profile = await fetchUserProfile(data.user.id);
        return { data: { ...data, profile }, error };
      }
      return { data, error };
    },
    studentSignIn,
    signOut: () => {
      if (studentUser) {
        setStudentUser(null);
        localStorage.removeItem(STUDENT_SESSION_KEY);
        return Promise.resolve({ error: null });
      }
      setProfile(null);
      return supabase.auth.signOut();
    },
    user: studentUser
      ? studentUser
      : user ? { ...user, role: profile?.role } : null,
    loading,
    isStudent: !!studentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(AuthContext);
};